import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { db } from "../firebase.config";
import { collection, getDocs, query, where } from "firebase/firestore";
import ListingItem from "../components/listings/ListingItem";
import Sidebar from "../components/sidebar/SideBar";
import ScaleSpinner from "../components/feedback/ScaleSpinner";
import SeoHead from "../seo/SeoHead";
import {
  buildItemListLd,
  buildBreadcrumbLd,
  buildFaqLd,
  buildOrganizationLd,
} from "../seo/StructuredData";
import {
  Box,
  Button,
  Heading,
  SimpleGrid,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  Flex,
  Container,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/react";
import WhatsaAppIcon from "../images/whatsapp.jpg";
import {
  ChevronRight,
  Home as HomeIcon,
  Building2,
  TrendingUp,
  Filter,
  X,
} from "lucide-react";

/* =========================================================
   CONSTANTS
   ========================================================= */
const SITE_URL = "https://www.estateghar.com"; // ⚠️ change to your real domain

const SUPPORTED_CITIES = [
  "karachi",
  "lahore",
  "islamabad",
  "rawalpindi",
  "faisalabad",
  "multan",
  "peshawar",
  "quetta",
  "gujranwala",
  "hyderabad",
  "sialkot",
  "bahawalpur",
];

/* =========================================================
   HELPERS
   ========================================================= */
const slugify = (s = "") =>
  s
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

const deslugify = (s = "") =>
  s
    .toString()
    .replace(/-/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

const isLocationMatch = (property, searchQuery) => {
  if (!searchQuery || searchQuery.trim() === "") return true;
  const searchTerm = searchQuery.toLowerCase().trim();

  const allLocationText = [
    property.location,
    property.society,
    property.area,
    property.sector,
    property.block,
    property.street,
    property.address,
    property.city,
    property.suburb,
    property.district,
    property.region,
    property.town,
    property.phase,
    `${property.society} ${property.area}`,
    `${property.sector} ${property.block}`,
    `${property.location} ${property.society}`,
    `${property.city} ${property.society}`,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (allLocationText.includes(searchTerm)) return true;

  const cleanText = allLocationText.replace(/[\s\-_.,/#!$%^&*;:{}=`~()]/g, "");
  const cleanSearch = searchTerm.replace(/[\s\-_.,/#!$%^&*;:{}=`~()]/g, "");
  if (cleanText.includes(cleanSearch)) return true;

  const searchWords = searchTerm.split(/\s+/);
  if (searchWords.length > 1) {
    const allWordsPresent = searchWords.every(
      (word) => allLocationText.includes(word) || cleanText.includes(word)
    );
    if (allWordsPresent) return true;
  }

  for (const word of searchWords) {
    if (word.length >= 3) {
      if (allLocationText.includes(word) || cleanText.includes(word)) return true;
    }
  }
  return false;
};

const isCityMatch = (property, cityQuery) => {
  if (!cityQuery || cityQuery.trim() === "") return true;
  const searchTerm = cityQuery.toLowerCase().trim();

  const allCityText = [
    property.city,
    property.location,
    property.society,
    property.area,
    property.sector,
    property.district,
    property.region,
    property.town,
    property.suburb,
    property.address,
    property.phase,
    property.block,
    `${property.city} ${property.location}`,
    `${property.society} ${property.city}`,
    `${property.area} ${property.city}`,
    `${property.city} ${property.society}`,
    `${property.location} ${property.city}`,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (allCityText.includes(searchTerm)) return true;

  const cleanText = allCityText.replace(/[\s\-_.,/#!$%^&*;:{}=`~()]/g, "");
  const cleanSearch = searchTerm.replace(/[\s\-_.,/#!$%^&*;:{}=`~()]/g, "");
  if (cleanText.includes(cleanSearch)) return true;

  const searchWords = searchTerm.split(/\s+/);
  if (searchWords.length > 1) {
    const allWordsPresent = searchWords.every(
      (word) => allCityText.includes(word) || cleanText.includes(word)
    );
    if (allWordsPresent) return true;
  }

  for (const word of searchWords) {
    if (word.length >= 2) {
      if (allCityText.includes(word) || cleanText.includes(word)) return true;
    }
  }
  return false;
};

/* =========================================================
   COMPONENT
   ========================================================= */
const Listings = ({ forcedListingType }) => {
  const { categoryName: categoryNameParam } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const navState = location.state || {};

  // Resolve listing type
  const resolvedType =
    forcedListingType ||
    categoryNameParam ||
    navState.listingType ||
    navState.categoryName ||
    "sale";

  const validListingType = ["sale", "rent"].includes(resolvedType)
    ? resolvedType
    : "sale";

  // 🔧 PARAM: city comes only from query string or location.state now
  const cityFromQuery = searchParams.get("city") || "";
  const cityFromState = navState.city || "";
  const initialCity = cityFromQuery || cityFromState || "";

  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [isFilterVisible, setIsFilterVisible] = useState(true);

  const [propertyType, setPropertyType] = useState(
    navState.propertyType || searchParams.get("type") || ""
  );
  const [city, setCity] = useState(initialCity);
  const [locationQuery, setLocationQuery] = useState(
    navState.location || searchParams.get("area") || ""
  );
  const [listingType, setListingType] = useState(validListingType);
  const [minPrice, setMinPrice] = useState(
    navState.minPrice || searchParams.get("min") || ""
  );
  const [maxPrice, setMaxPrice] = useState(
    navState.maxPrice || searchParams.get("max") || ""
  );
  const [priceRange, setPriceRange] = useState(navState.priceRange || "");

  const hasHydratedRef = useRef(false);

  const whatsappURL = "https://wa.me/+923487172565";
  const contact = () => window.open(whatsappURL, "_blank");

  /* ---------------- NORMALIZER ---------------- */
  const normalizeProperty = (doc) => {
    const d = doc.data();
    const imagesArray = Array.isArray(d.images)
      ? d.images
      : Array.isArray(d.propertyImages)
      ? d.propertyImages
      : [];
    const thumbnail = d.thumbnail || imagesArray[0] || "";

    return {
      id: doc.id,
      data: {
        ...d,
        price: Number(d.price) || 0,
        bedrooms: Number(d.bedrooms) || 0,
        bathrooms: Number(d.bathrooms) || 0,
        area: Number(d.area) || 0,
        marla: Number(d.marla) || 0,
        kanal: Number(d.kanal) || 0,

        location: d.location || d.city || "",
        city: d.city || d.location || "",
        society: d.society || d.area || "",
        sector: d.sector || "",
        block: d.block || "",
        suburb: d.suburb || "",
        district: d.district || "",
        region: d.region || "",
        town: d.town || "",
        phase: d.phase || "",
        streetNo: d.streetNo || "",
        houseNo: d.houseNo || "",
        propertyType: d.propertyType || "residential",
        propertyCategory: d.propertyCategory || "residential",

        images: imagesArray,
        propertyImages: imagesArray,
        thumbnail: thumbnail,

        agentName: d.agentName || "",
        agentPhone: d.agentPhone || d.phoneNumber || "",
        agentCompany: d.agentCompany || "",
        whatsappNumber: d.whatsappNumber || "",

        features: d.features || {},
        nearbyAmenities: d.nearbyAmenities || {},

        offer: d.offer || false,
        discountedPrice: d.discountedPrice || 0,

        isFeatured: Boolean(d.featured || d.isFeatured),

        sortDate:
          d.timestamp?.toMillis?.() ||
          d.createdAt?.toMillis?.() ||
          d.updatedAt?.toMillis?.() ||
          Date.now(),
      },
    };
  };

  /* ---------------- FETCH ---------------- */
  useEffect(() => {
    let cancelled = false;

    const fetchProperties = async () => {
      setLoading(true);

      try {
        const ref = collection(db, "properties");
        const q = query(ref, where("type", "==", listingType));
        const snap = await getDocs(q);

        let list = snap.docs
          .map((doc) => {
            try {
              return normalizeProperty(doc);
            } catch (err) {
              console.error("Error normalizing document:", doc.id, err);
              return null;
            }
          })
          .filter(Boolean);

        list = list.filter((p) => {
          const d = p.data;
          if (propertyType && d.propertyType !== propertyType) return false;
          if (city && !isCityMatch(d, city)) return false;
          if (locationQuery && !isLocationMatch(d, locationQuery)) return false;
          if (minPrice && d.price < Number(minPrice)) return false;
          if (maxPrice && d.price > Number(maxPrice)) return false;
          return true;
        });

        list.sort(
          (a, b) =>
            (b.data.isFeatured ? 1 : 0) - (a.data.isFeatured ? 1 : 0) ||
            b.data.sortDate - a.data.sortDate
        );

        if (!cancelled) setProperties(list);
      } catch (err) {
        console.error("Fetch error:", err);
        if (!cancelled) setProperties([]);
      }

      if (!cancelled) setLoading(false);
    };

    fetchProperties();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingType, propertyType, city, locationQuery, minPrice, maxPrice]);

  /* ---------------- SYNC listingType with resolved URL type ---------------- */
  useEffect(() => {
    if (validListingType !== listingType) setListingType(validListingType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validListingType]);

  /* ---------------- SYNC city when query string changes (e.g. back/forward) ---------------- */
  useEffect(() => {
    const nextCity = cityFromQuery || "";
    if (nextCity !== city) setCity(nextCity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityFromQuery]);

  /* ---------------- Re-hydrate from location.state on new navigation ---------------- */
  useEffect(() => {
    if (!location.state) return;
    const s = location.state;
    if (s.propertyType !== undefined) setPropertyType(s.propertyType || "");
    if (s.city !== undefined) setCity(s.city || "");
    if (s.location !== undefined) setLocationQuery(s.location || "");
    if (s.minPrice !== undefined) setMinPrice(s.minPrice || "");
    if (s.maxPrice !== undefined) setMaxPrice(s.maxPrice || "");
    if (s.priceRange !== undefined) setPriceRange(s.priceRange || "");
    if (s.listingType && ["sale", "rent"].includes(s.listingType)) {
      setListingType(s.listingType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  /* ---------------- SYNC FILTERS → URL (city stays in query now) ---------------- */
  useEffect(() => {
    if (!hasHydratedRef.current) {
      hasHydratedRef.current = true;
      return;
    }

    const next = new URLSearchParams(searchParams);

    propertyType ? next.set("type", propertyType) : next.delete("type");
    locationQuery ? next.set("area", locationQuery) : next.delete("area");
    minPrice ? next.set("min", minPrice) : next.delete("min");
    maxPrice ? next.set("max", maxPrice) : next.delete("max");

    // 🔧 PARAM: always write city into query string
    city ? next.set("city", slugify(city)) : next.delete("city");

    const nextStr = next.toString();
    const currentStr = searchParams.toString();
    if (nextStr !== currentStr) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyType, locationQuery, minPrice, maxPrice, city]);

  /* ---------------- CLEAR FILTERS ---------------- */
  const clearAllFilters = () => {
    setPropertyType("");
    setLocationQuery("");
    setMinPrice("");
    setMaxPrice("");
    setPriceRange("");
    setCity(""); // 🔧 PARAM: clear city query too
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (propertyType) count++;
    if (locationQuery) count++;
    if (minPrice) count++;
    if (maxPrice) count++;
    if (city) count++; // 🔧 PARAM: city counts as a filter
    return count;
  };

  /* =========================================================
     SEO / DISPLAY VALUES
     ========================================================= */
  const cityName = city ? deslugify(slugify(city)) : "";
  const listingTypeLabel = listingType === "rent" ? "Rent" : "Sale";
  const categoryLabel = `Properties for ${listingTypeLabel}`;

  const pageTitle = cityName
    ? `${categoryLabel} in ${cityName} | EstateGhar`
    : `${categoryLabel} in Pakistan | Verified Listings | EstateGhar`;

  const metaDescription = cityName
    ? `Browse verified properties for ${listingTypeLabel.toLowerCase()} in ${cityName}. Houses, plots, flats & commercial with real photos, prices and direct agent contact. Updated daily on EstateGhar.`
    : `Find verified properties for ${listingTypeLabel.toLowerCase()} across Pakistan — Karachi, Lahore, Islamabad & more. Real photos, live prices, direct agent contact. Updated daily.`;

  // 🔧 PARAM: canonical is always the base page (no query, to avoid duplicate content)
  const canonicalPath = `/properties-for-${listingType}`;

  // 🔧 PARAM: any active filter (city, area, type, price) → noindex
  const hasFilters =
    Boolean(propertyType) ||
    Boolean(locationQuery) ||
    Boolean(minPrice) ||
    Boolean(maxPrice) ||
    Boolean(city);

  const robotsMeta = hasFilters ? "noindex, follow" : "index, follow";

  /* ---------- JSON-LD ---------- */
  const jsonLd = useMemo(() => {
    const ld = [
      buildOrganizationLd(),
      buildBreadcrumbLd({
        listingType,
        cityName,
        citySlug: city ? slugify(city) : "",
      }),
    ];

    if (properties.length > 0) {
      ld.push(
        buildItemListLd({ listingType, cityName, properties }),
        buildFaqLd({ listingType, cityName, count: properties.length })
      );
    }
    return ld;
  }, [listingType, cityName, properties]);

  /* =========================================================
     HEADER UI HELPERS
     ========================================================= */
  const getCategoryIcon = () => {
    switch (listingType) {
      case "sale":
        return <TrendingUp size={24} />;
      case "rent":
        return <Building2 size={24} />;
      default:
        return <HomeIcon size={24} />;
    }
  };

  const getHeaderAction = () => {
    if (listingType === "rent") {
      return (
        <Box
          bgGradient="linear(to-r, blue.50, teal.50)"
          borderRadius="xl"
          p={4}
          mb={6}
          borderWidth="1px"
          borderColor="blue.100"
        >
          <Flex
            direction={{ base: "column", md: "row" }}
            align="center"
            justify="center"
            gap={4}
          >
            <Text fontSize="md" fontWeight="medium" color="gray.700">
              🏠 Need help finding a rental property?
            </Text>
            <Button
              onClick={contact}
              bg="green.500"
              color="white"
              _hover={{ bg: "green.600" }}
              leftIcon={
                <img
                  src={WhatsaAppIcon}
                  style={{ width: "20px", height: "20px" }}
                  alt="Whatsapp"
                />
              }
            >
              Contact Agent on WhatsApp
            </Button>
          </Flex>
        </Box>
      );
    }

    return (
      <Box
        bgGradient="linear(to-r, teal.50, green.50)"
        borderRadius="xl"
        p={4}
        mb={6}
        borderWidth="1px"
        borderColor="teal.100"
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          align="center"
          justify="center"
          gap={4}
        >
          <Text fontSize="md" fontWeight="medium" color="gray.700">
            📢 Looking to sell your property?
          </Text>
          <Link to="/create-listing">
            <Button
              colorScheme="teal"
              size="md"
              rightIcon={<ChevronRight size={18} />}
            >
              List Your Property
            </Button>
          </Link>
        </Flex>
      </Box>
    );
  };

  /* =========================================================
     RENDER
     ========================================================= */
  return (
    <Box bg="gray.50" minH="100vh">
      <SeoHead
        title={pageTitle}
        description={metaDescription}
        canonicalPath={canonicalPath}
        robots={robotsMeta}
        jsonLd={jsonLd}
      />

      <Container maxW="container.xl" py={8}>
        <Breadcrumb
          spacing="8px"
          separator={<ChevronRight size={16} color="gray.500" />}
          mb={6}
        >
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to="/">
              <Flex align="center" gap={1}>
                <HomeIcon size={16} />
                <Text>Home</Text>
              </Flex>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to={`/properties-for-${listingType}`}>
              {categoryLabel}
            </BreadcrumbLink>
          </BreadcrumbItem>
          {cityName && (
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink href="#">{cityName}</BreadcrumbLink>
            </BreadcrumbItem>
          )}
        </Breadcrumb>

        {getHeaderAction()}

        <Box textAlign="center" mb={8}>
          <Flex align="center" justify="center" gap={3} mb={4}>
            <Box
              p={3}
              borderRadius="full"
              bgGradient="linear(to-r, teal.500, green.500)"
              color="white"
            >
              {getCategoryIcon()}
            </Box>
            <Heading
              as="h1"
              fontSize={{ base: "3xl", md: "4xl" }}
              bgGradient="linear(to-r, teal.600, green.600)"
              bgClip="text"
            >
              {cityName ? `${categoryLabel} in ${cityName}` : categoryLabel}
            </Heading>
          </Flex>
          <Text color="gray.600" maxW="2xl" mx="auto">
            {cityName
              ? `Discover the best ${listingTypeLabel.toLowerCase()} properties in ${cityName}. Use filters to find your perfect match.`
              : `Discover the best ${listingTypeLabel.toLowerCase()} properties in Pakistan. Use filters to find your perfect match.`}
          </Text>
        </Box>

        <Box display={{ base: "block", lg: "none" }} mb={4}>
          <Button
            leftIcon={<Filter size={18} />}
            rightIcon={
              <Badge
                colorScheme="teal"
                borderRadius="full"
                px={2}
                display={getActiveFilterCount() > 0 ? "inline-flex" : "none"}
              >
                {getActiveFilterCount()}
              </Badge>
            }
            onClick={() => setIsFilterVisible(!isFilterVisible)}
            w="full"
            variant="outline"
            colorScheme="teal"
          >
            {isFilterVisible ? "Hide Filters" : "Show Filters"}
          </Button>
        </Box>

        <Flex direction={{ base: "column", lg: "row" }} gap={6}>
          <Box
            className={isFilterVisible ? "block" : "hidden lg:block"}
            w={{ base: "full", lg: "320px" }}
            flexShrink={0}
          >
            <Sidebar
              propertyType={propertyType}
              city={city}
              location={locationQuery}
              listingType={listingType}
              priceRange={priceRange}
              minPrice={minPrice}
              maxPrice={maxPrice}
              setPropertyType={setPropertyType}
              setCity={setCity}
              setLocation={setLocationQuery}
              setListingType={setListingType}
              setPriceRange={setPriceRange}
              setMinPrice={setMinPrice}
              setMaxPrice={setMaxPrice}
            />
          </Box>

          <Box flex={1}>
            {getActiveFilterCount() > 0 && (
              <Box
                bg="white"
                borderRadius="lg"
                p={4}
                mb={6}
                borderWidth="1px"
                borderColor="gray.200"
              >
                <Flex align="center" justify="space-between" wrap="wrap" gap={3}>
                  <Flex align="center" gap={2} wrap="wrap">
                    <Text fontWeight="semibold" color="gray.700">
                      Active Filters:
                    </Text>
                    {propertyType && (
                      <Badge colorScheme="teal" px={3} py={1} borderRadius="full">
                        {propertyType.replace(/-/g, " ")}
                        <X
                          size={12}
                          style={{
                            display: "inline",
                            marginLeft: "8px",
                            cursor: "pointer",
                          }}
                          onClick={() => setPropertyType("")}
                        />
                      </Badge>
                    )}
                    {city && (
                      <Badge colorScheme="blue" px={3} py={1} borderRadius="full">
                        {cityName}
                        <X
                          size={12}
                          style={{
                            display: "inline",
                            marginLeft: "8px",
                            cursor: "pointer",
                          }}
                          onClick={() => setCity("")}
                        />
                      </Badge>
                    )}
                    {locationQuery && (
                      <Badge
                        colorScheme="purple"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        Area: {locationQuery}
                        <X
                          size={12}
                          style={{
                            display: "inline",
                            marginLeft: "8px",
                            cursor: "pointer",
                          }}
                          onClick={() => setLocationQuery("")}
                        />
                      </Badge>
                    )}
                    {(minPrice || maxPrice) && (
                      <Badge colorScheme="green" px={3} py={1} borderRadius="full">
                        PKR {minPrice ? Number(minPrice).toLocaleString() : "0"} -{" "}
                        {maxPrice ? Number(maxPrice).toLocaleString() : "Any"}
                        <X
                          size={12}
                          style={{
                            display: "inline",
                            marginLeft: "8px",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setMinPrice("");
                            setMaxPrice("");
                            setPriceRange("");
                          }}
                        />
                      </Badge>
                    )}
                  </Flex>
                  <Button
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={clearAllFilters}
                  >
                    Clear All
                  </Button>
                </Flex>
              </Box>
            )}

            {!loading && properties.length > 0 && (
              <Flex align="center" justify="space-between" mb={4} wrap="wrap" gap={3}>
                <Flex align="center" gap={2}>
                  <Badge
                    colorScheme="teal"
                    fontSize="md"
                    px={3}
                    py={1}
                    borderRadius="full"
                  >
                    {properties.length} Properties Found
                  </Badge>
                  <Text fontSize="sm" color="gray.500">
                    {listingType === "sale" ? "✅ For Sale" : "🔑 For Rent"}
                  </Text>
                </Flex>
              </Flex>
            )}

            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="500px"
                bg="white"
                borderRadius="2xl"
              >
                <ScaleSpinner />
              </Box>
            ) : properties.length > 0 ? (
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 2, xl: 3 }} spacing={6}>
                {properties.map((property, idx) => (
                  <Box
                    key={property.id}
                    transition="all 0.3s"
                    _hover={{ transform: "translateY(-4px)" }}
                    height="100%"
                  >
                    <ListingItem
                      id={property.id}
                      property={property.data}
                      category={listingType}
                      priority={idx < 6}
                    />
                  </Box>
                ))}
              </SimpleGrid>
            ) : (
              <Alert
                status="info"
                variant="subtle"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                height="400px"
                borderRadius="2xl"
                bg="white"
                borderWidth="1px"
                borderColor="gray.200"
              >
                <Box p={4} borderRadius="full" bg="blue.50" mb={4}>
                  <HomeIcon size={48} color="#4299E1" />
                </Box>
                <AlertIcon boxSize="40px" mr={0} color="blue.500" />
                <AlertTitle mt={4} mb={2} fontSize="xl">
                  No properties found!
                </AlertTitle>
                <AlertDescription maxWidth="md" color="gray.600">
                  Sorry, we couldn't find any {listingType} properties
                  {cityName ? ` in ${cityName}` : ""} matching your criteria.
                  Try adjusting your filters or clearing them to see more
                  properties.
                </AlertDescription>
                <Button
                  mt={6}
                  colorScheme="teal"
                  onClick={clearAllFilters}
                  rightIcon={<X size={18} />}
                >
                  Clear All Filters
                </Button>
              </Alert>
            )}

            {/* ======================================================
                SEO INTERNAL LINKING BLOCK — now uses ?city= param
                ====================================================== */}
            <Box mt={12} pt={8} borderTopWidth="1px" borderColor="gray.200">
              <Heading as="h2" size="md" mb={4} color="gray.700">
                Browse {categoryLabel} by City
              </Heading>
              <Flex wrap="wrap" gap={3} mb={8}>
                {SUPPORTED_CITIES.map((slug) => (
                  <Link
                    key={slug}
                    to={`/properties-for-${listingType}?city=${slug}`}
                    aria-label={`${categoryLabel} in ${deslugify(slug)}`}
                  >
                    <Badge
                      colorScheme={slug === slugify(city) ? "teal" : "gray"}
                      px={3}
                      py={2}
                      borderRadius="full"
                      fontSize="sm"
                      _hover={{ bg: "teal.100" }}
                      cursor="pointer"
                    >
                      {deslugify(slug)}
                    </Badge>
                  </Link>
                ))}
              </Flex>

              <Heading as="h2" size="md" mb={4} color="gray.700">
                Explore Other Listings
              </Heading>
              <Flex wrap="wrap" gap={4}>
                <Link to="/properties-for-sale">
                  <Button variant="outline" colorScheme="teal" size="sm">
                    Properties for Sale in Pakistan
                  </Button>
                </Link>
                <Link to="/properties-for-rent">
                  <Button variant="outline" colorScheme="blue" size="sm">
                    Properties for Rent in Pakistan
                  </Button>
                </Link>
                {city && (
                  <>
                    <Link to={`/properties-for-sale?city=${slugify(city)}`}>
                      <Button variant="outline" colorScheme="teal" size="sm">
                        For Sale in {cityName}
                      </Button>
                    </Link>
                    <Link to={`/properties-for-rent?city=${slugify(city)}`}>
                      <Button variant="outline" colorScheme="blue" size="sm">
                        For Rent in {cityName}
                      </Button>
                    </Link>
                  </>
                )}
              </Flex>
            </Box>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default Listings;